import { useNavigate } from "react-router-dom"
import { useRef, useState, useEffect } from "react"
import LandingHeader from "./LandingHeader"
import AccentBar from "../ui/AccentBar"
import EditableText from "../ui/EditableText"

const applyOptions = ["Trademark", "Patent", "Design"]

const DEFAULT_HEADING = "EUROPEAN IP FILING, MADE SIMPLE"
const DEFAULT_INTRO =
  "File your patent, trademark or design in Europe through one simple " +
  "online process. xpress-ip combines the convenience of digital filing " +
  "with professional representation before the European IP offices in " +
  "cooperation with experienced European Patent Attorneys at DANTZ IP & LAW."
const DEFAULT_TAGLINE = "Simple. Fast. Professional. Predictable."

interface LandingHeroProps {
  heading?: string
  intro?: string
  tagline?: string
  editable?: boolean
  onSaveHeading?: (value: string) => void
  onSaveIntro?: (value: string) => void
  onSaveTagline?: (value: string) => void
}

export default function LandingHero({
  heading = DEFAULT_HEADING,
  intro = DEFAULT_INTRO,
  tagline = DEFAULT_TAGLINE,
  editable = false,
  onSaveHeading,
  onSaveIntro,
  onSaveTagline
}: LandingHeroProps) {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <section className="relative flex min-h-[640px] flex-col items-center pb-36">
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
      >
        <source src="/videos/xpress-hero.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-dantzBlue/50" />

      <AccentBar position="top" />

      <LandingHeader />

      <div className="relative z-10 mx-auto mt-14 max-w-3xl px-6 text-center text-white">
        <EditableText
          as="h1"
          value={heading}
          editMode={editable}
          onSave={(value) => onSaveHeading?.(value)}
          className="text-[32px] font-normal leading-tight tracking-widest md:text-[40px]"
        />
        <EditableText
          as="p"
          value={intro}
          editMode={editable}
          multiline
          onSave={(value) => onSaveIntro?.(value)}
          className="mt-5 text-sm font-light leading-relaxed text-slate-200"
        />
        <EditableText
          as="p"
          value={tagline}
          editMode={editable}
          onSave={(value) => onSaveTagline?.(value)}
          className="mt-4 text-sm font-medium tracking-wide"
        />
      </div>

      <div className="relative z-10 mt-10 flex flex-col items-center" ref={menuRef}>
        <button onClick={() => setMenuOpen(!menuOpen)} aria-label="Apply now">
          <img src="/images/btn-apply-now.svg" alt="Apply now" />
        </button>

        <div
          className={`absolute left-1/2 top-full mt-2 flex w-fit -translate-x-1/2 flex-col items-center gap-1 rounded-2xl bg-white/80 px-9 py-3 text-dantzBlue backdrop-blur-sm transition-all duration-200 ease-out ${
            menuOpen ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0 pointer-events-none"
          }`}
        >
          {applyOptions.map(option => (
            <button
              key={option}
              onClick={() => navigate("/login")}
              className="tracking-wide underline-offset-4 hover:underline"
            >
              {option.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
